import type { CSSProperties } from 'react';
import { adminButton, adminField, adminTokens as a } from '../../admin-ui';

export const PAGE_SIZE_OPTIONS = [25, 50, 100] as const;
export type AllowedPageSize = (typeof PAGE_SIZE_OPTIONS)[number];

interface ListPaginationProps {
  page: number;
  pageSize: AllowedPageSize;
  totalCount: number;
  isPlaceholderData: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: AllowedPageSize) => void;
}

// Used only by the management lists, so it follows the admin recipes.
const containerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: a.spacing.md,
  padding: `${a.spacing.xs} ${a.spacing.xs}`,
  flexWrap: 'wrap',
};

const textStyle: CSSProperties = {
  fontSize: a.typography.label.size,
  color: a.text.secondary,
  whiteSpace: 'nowrap',
};

const buttonStyle: CSSProperties = {
  ...adminButton.secondary,
  minHeight: '32px',
  padding: `0 ${a.spacing.md}`,
  fontSize: a.typography.label.size,
};

const buttonDisabledStyle: CSSProperties = {
  ...buttonStyle,
  ...adminButton.disabled,
  cursor: 'default',
};

const selectStyle: CSSProperties = {
  ...adminField.select,
  height: '32px',
  fontSize: a.typography.label.size,
};

export function ListPagination({
  page,
  pageSize,
  totalCount,
  isPlaceholderData,
  onPageChange,
  onPageSizeChange,
}: ListPaginationProps) {
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  const canPrev = page > 1 && !isPlaceholderData;
  const canNext = page < totalPages && !isPlaceholderData;

  return (
    <div style={containerStyle} role="navigation" aria-label="Pagination">
      <span style={textStyle}>
        {totalCount === 0 ? '0 results' : `${start}–${end} of ${totalCount}`}
      </span>

      <div style={{ display: 'flex', gap: a.spacing.xs, alignItems: 'center' }}>
        <button
          type="button"
          style={canPrev ? buttonStyle : buttonDisabledStyle}
          onClick={() => canPrev && onPageChange(page - 1)}
          disabled={!canPrev}
          aria-label="Previous page"
        >
          ‹ Prev
        </button>
        <span style={textStyle}>
          {page} / {totalPages}
        </span>
        <button
          type="button"
          style={canNext ? buttonStyle : buttonDisabledStyle}
          onClick={() => canNext && onPageChange(page + 1)}
          disabled={!canNext}
          aria-label="Next page"
        >
          Next ›
        </button>
      </div>

      <label style={{ display: 'flex', alignItems: 'center', gap: a.spacing.xs }}>
        <span style={textStyle}>Per page:</span>
        <select
          style={selectStyle}
          value={pageSize}
          onChange={(e) => onPageSizeChange(Number(e.target.value) as AllowedPageSize)}
          aria-label="Results per page"
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </label>
    </div>
  );
}
